// Shared My Calendar event configuration — used by pages/my/MyCalendarPage.js
// and pages/workspace/MyCalendarWidget.js so icons/filters/legend stay in sync.
//
// Events are grouped into the two main CRM stages:
//   Quotation  — quotation-stage estimate events
//   Enter Job  — enter job + change order and all CO sub-items
//     (CO Estimate, APD, Shop/Erection Drawing, A/B for Erection,
//      Forecast, Design Calculation — backend emits them as co_* types)
import {
  FaCalendarAlt, FaPhone, FaEnvelope, FaSms, FaEllipsisH, FaPlane, FaCar,
} from 'react-icons/fa';
import {
  MdPeople, MdAssignment, MdCheckCircle, MdWork, MdSyncAlt, MdGavel,
  MdRequestQuote, MdSend, MdPublish, MdSchedule,
  MdApproval, MdArchitecture, MdEngineering, MdConstruction,
  MdTrendingUp, MdCalculate,
} from 'react-icons/md';

// Icon per exact event type — used in calendar event chips
export const EVENT_ICONS = {
  'activity-meeting':  MdPeople,
  'activity-call':     FaPhone,
  'activity-email':    FaEnvelope,
  'activity-sms':      FaSms,
  'activity-other':    FaEllipsisH,
  'task':              MdAssignment,
  // Quotation-stage estimate events
  'request_estimate':             MdSend,
  'quotation_request_estimate':   MdSend,
  'schedule_estimate':            MdSchedule,
  'quotation_schedule_estimate':  MdSchedule,
  'release_estimate':             MdPublish,
  'quotation_release_estimate':   MdPublish,
  // Enter Job stage
  'enter_job':                    MdWork,
  'create_enter_job':             MdWork,
  'change_order':                 MdSyncAlt,
  'confirm_change_order':         MdSyncAlt,
  // Bookings
  'travel': FaPlane,
  'car':    FaCar,
};

// Change-order sub-item types arrive with milestone suffixes
// (co_apd_requested / co_apd_scheduled / ...) — match by prefix
const CO_ICON_PREFIXES = [
  ['co_estimate',           MdRequestQuote],
  ['co_apd',                MdApproval],
  ['co_shop_drawing',       MdArchitecture],
  ['co_erection_drawing',   MdEngineering],
  ['co_ab_erection',        MdConstruction],
  ['co_forecast',           MdTrendingUp],
  ['co_design_calculation', MdCalculate],
  ['co_other',              MdSyncAlt],
];

// Project reference shown on project-related events, highest priority first:
// job number → quote number → PCV number. Accepts either the raw event
// (snake_case: j_number/q_number/pcv_number) or the detail-modal shape
// (camelCase: jNumber/qNumber/pcvNumber). Returns null for non-project events
// (activities, tasks, bookings) which carry none of these.
export const getProjectRef = (ev = {}) => (
  ev.j_number   || ev.jNumber ||
  ev.q_number   || ev.qNumber ||
  ev.pcv_number || ev.pcvNumber ||
  null
);

export const getEventIcon = (type, fallback = FaCalendarAlt) => {
  if (EVENT_ICONS[type]) return EVENT_ICONS[type];
  const co = CO_ICON_PREFIXES.find(([prefix]) => type?.startsWith(prefix));
  if (co) return co[1];
  if (type?.startsWith('waiver')) return MdGavel;
  return fallback;
};

// Sidebar filter buttons. Matching uses startsWith, so the 'co_' entry
// captures every change-order sub-item type under Enter Job.
export const FILTER_GROUPS = [
  { key: 'activity',  color: '#3b82f6', label: 'Activities', Icon: MdPeople,       types: ['activity-meeting', 'activity-call', 'activity-email', 'activity-sms', 'activity-other'] },
  { key: 'task',      color: '#6366f1', label: 'Tasks',      Icon: MdAssignment,   types: ['task'] },
  { key: 'quotation', color: '#f59e0b', label: 'Quotation',  Icon: MdRequestQuote, types: ['quotation_request_estimate', 'quotation_schedule_estimate', 'quotation_release_estimate', 'request_estimate', 'schedule_estimate', 'release_estimate'] },
  { key: 'enter_job', color: '#ef4444', label: 'Enter Job',  Icon: MdWork,         types: ['enter_job', 'create_enter_job', 'change_order', 'confirm_change_order', 'co_'] },
  { key: 'waiver',    color: '#7c3aed', label: 'Waiver',     Icon: MdGavel,        types: ['waiver'] },
  { key: 'booking',   color: '#0ea5e9', label: 'Bookings',   Icon: FaPlane,        types: ['travel', 'car'] },
];

// Legend grouped by stage — each sub-type keeps its own icon/color
export const LEGEND_SECTIONS = [
  {
    heading: 'Activities',
    items: [
      { color: '#3b82f6', label: 'Meeting', Icon: MdPeople },
      { color: '#10b981', label: 'Call',    Icon: FaPhone },
      { color: '#8b5cf6', label: 'Email',   Icon: FaEnvelope },
    ],
  },
  {
    heading: 'Tasks',
    items: [
      { color: '#6366f1', label: 'Task (Todo)', Icon: MdAssignment },
      { color: '#94a3b8', label: 'Task (Done)', Icon: MdCheckCircle },
    ],
  },
  {
    heading: 'Quotation',
    items: [
      { color: '#f59e0b', label: 'Request Estimate',  Icon: MdSend },
      { color: '#f97316', label: 'Schedule Estimate', Icon: MdSchedule },
      { color: '#059669', label: 'Release Estimate',  Icon: MdPublish },
    ],
  },
  {
    heading: 'Enter Job',
    items: [
      { color: '#ef4444', label: 'Enter Job',          Icon: MdWork },
      { color: '#ec4899', label: 'Change Order',       Icon: MdSyncAlt },
      { color: '#db2777', label: 'CO Estimate',        Icon: MdRequestQuote },
      { color: '#f472b6', label: 'APD',                Icon: MdApproval },
      { color: '#e11d48', label: 'Shop Drawing',       Icon: MdArchitecture },
      { color: '#fb7185', label: 'Erection Drawing',   Icon: MdEngineering },
      { color: '#be123c', label: 'A/B for Erection',   Icon: MdConstruction },
      { color: '#c026d3', label: 'Forecast',           Icon: MdTrendingUp },
      { color: '#a21caf', label: 'Design Calculation', Icon: MdCalculate },
    ],
  },
  {
    heading: 'Waiver',
    items: [{ color: '#7c3aed', label: 'Waiver', Icon: MdGavel }],
  },
  {
    heading: 'Bookings',
    items: [
      { color: '#3b82f6', label: 'Travel',      Icon: FaPlane },
      { color: '#10b981', label: 'Car Booking', Icon: FaCar },
    ],
  },
];
